import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import type { AuditFields, Page } from './types';

export interface Inspector extends AuditFields {
  id: string;
  version: number;
  titleBefore: string | null;
  firstName: string;
  lastName: string;
  titleAfter: string | null;
  displayName: string;
  personalNumber: string;
  personalNumberMasked: boolean;
  phone: string;
  email: string;
  linkedToUser: boolean;
}

export interface InspectorSummary {
  id: string;
  displayName: string;
  email: string;
}

export interface InspectorRequest {
  titleBefore: string;
  firstName: string;
  lastName: string;
  titleAfter: string;
  personalNumber: string;
  phone: string;
  email: string;
  version?: number;
}

export function useInspectors(query: { q?: string; page: number; size: number; sort: string }) {
  return useQuery({
    queryKey: ['inspectors', query],
    queryFn: ({ signal }) => api<Page<Inspector>>('/inspectors', { query: { ...query }, signal }),
    placeholderData: keepPreviousData,
  });
}

export function useInspector(id: string | undefined) {
  return useQuery({ queryKey: ['inspectors', id], queryFn: () => api<Inspector>(`/inspectors/${id}`), enabled: Boolean(id) });
}

export function searchInspectors(q: string, signal?: AbortSignal): Promise<InspectorSummary[]> {
  return api<InspectorSummary[]>('/inspectors/search', { query: { q }, signal });
}

export function useSaveInspector(id?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (request: InspectorRequest) =>
      id
        ? api<Inspector>(`/inspectors/${id}`, { method: 'PUT', body: request, version: request.version })
        : api<Inspector>('/inspectors', { method: 'POST', body: request }),
    onSuccess: (inspector) => {
      qc.setQueryData(['inspectors', inspector.id], inspector);
      void qc.invalidateQueries({ queryKey: ['inspectors'] });
    },
  });
}

export function useDeleteInspector() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (i: { id: string; version: number }) => api<void>(`/inspectors/${i.id}`, { method: 'DELETE', version: i.version }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['inspectors'] }),
  });
}
